import React, {FC, useMemo} from 'react'
import {GoodsReceipt, GoodsReceiptDetail} from '../core/_models'
import {DocBlock, DocField, DocFieldGrid} from './ProcurementDocumentSections'
import ProcurementAttachmentPrintList from './ProcurementAttachmentPrintList'
import {useProcurementBrandingAssets} from './ProcurementDocumentBranding'
import {
  getContextHeaderLabels,
  getEquipmentNameFromLine,
  getItemDisplayNameFromLine,
  getLinkedDefectDisplay,
  getPartNameFromLine,
  getSubcomponentNameFromLine,
  resolveRequisitionViewContext,
  uniqueJoinedValues,
} from '../core/technicalItemContext'

interface GoodsReceiptGRNDocumentProps {
  receipt: GoodsReceiptDetail | GoodsReceipt
}

const displayValue = (value: any) => {
  const text = String(value ?? '').trim()
  return text ? text : '-'
}

const GoodsReceiptGRNDocument: FC<GoodsReceiptGRNDocumentProps> = ({receipt}) => {
  const receiptAny = receipt as any
  const {logoSrc: companyLogoUrl, watermarkSrc: companyWatermarkUrl, ready: brandingReady} =
    useProcurementBrandingAssets(
      receiptAny?.companyLogoUrl ||
        receiptAny?.logoUrl,
      receiptAny?.companyWatermarkUrl ||
        receiptAny?.watermarkUrl,
      receiptAny?.companyGroupAdminId || null
    )

  const companyName = displayValue(
    receiptAny?.companyName
  )
  const vesselName = displayValue(receiptAny?.vesselName)
  const requisitionCode = displayValue(
    receiptAny?.requisitionNo
  )
  const linkedDefect = displayValue(getLinkedDefectDisplay(receiptAny))
  const categoryName = displayValue(receiptAny?.categoryName)
  const context = resolveRequisitionViewContext(
    receiptAny?.requestFor,
    categoryName
  )
  const {primary: contextPrimaryLabel, secondary: contextSecondaryLabel} =
    getContextHeaderLabels(context)

  const resolvedLines = useMemo(
    () =>
      (receipt.lines || []).map((line: any, index: number) => {
        return {
          ...line,
          itemDescription: getItemDisplayNameFromLine(line, index),
          equipmentName: getEquipmentNameFromLine(line) || '-',
          subcomponentName: getSubcomponentNameFromLine(line) || '-',
          partName: getPartNameFromLine(line) || '-',
          partNo: displayValue(line?.partNumber),
          drawingNo: displayValue(line?.drawingNo),
        }
      }),
    [receipt.lines]
  )

  const servicesHead = displayValue(
    receiptAny?.inventoryItemHeadName
  )
  const servicesSubHead = displayValue(
    receiptAny?.inventoryItemSubHeadName
  )
  const subCategory = displayValue(
    receiptAny?.inventoryItemSubCategoryName
  )
  const equipmentSummary = uniqueJoinedValues(resolvedLines.map((line) => line.equipmentName))
  const subcomponentSummary = uniqueJoinedValues(resolvedLines.map((line) => line.subcomponentName))
  const contextPrimaryValue =
    context === 'TECHNICAL' ? equipmentSummary : context === 'SERVICES' ? servicesHead : categoryName
  const contextSecondaryValue =
    context === 'TECHNICAL'
      ? subcomponentSummary
      : context === 'SERVICES'
      ? servicesSubHead
      : subCategory

  const rootClassName = `procurement-doc grn-doc${companyWatermarkUrl ? ' procurement-has-watermark' : ''}`

  return (
    <div className={rootClassName} data-export-ready={brandingReady ? 'true' : 'false'}>
      {companyWatermarkUrl ? (
        <div className='procurement-doc-watermark' aria-hidden='true'>
          <img
            src={companyWatermarkUrl}
            alt=''
            className='procurement-doc-watermark-image'
            crossOrigin='anonymous'
            data-pdf-watermark-source='true'
          />
        </div>
      ) : null}
      <div className='procurement-doc-content'>
        {companyLogoUrl ? (
          <div className='procurement-doc-brand-header mb-3'>
            <img
              src={companyLogoUrl}
              alt='Company Logo'
              className='procurement-doc-brand-logo'
              crossOrigin='anonymous'
              data-pdf-logo='true'
            />
          </div>
        ) : null}

        <DocBlock title='Goods Receipt Header'>
            <DocFieldGrid columns={4}>
              <DocField label='GRN' value={`GRN-${receipt.id}`} />
            <DocField label='PO' value={receiptAny?.purchaseOrderNumber || `PO-${receipt.purchaseOrderId}`} />
            <DocField label='Company' value={companyName} />
            <DocField label='Vessel' value={vesselName} />
            <DocField label='Received Date' value={displayValue(receipt.receivedDate)} />
            <DocField label='Received By' value={displayValue(receipt.receivedBy)} />
            <DocField label='Status' value={displayValue(receipt.status)} />
            <DocField label='Discrepancy' value={receipt.discrepancy ?? '-'} />
          </DocFieldGrid>
        </DocBlock>

        <DocBlock title='Requisition / RFQ Context'>
          <DocFieldGrid columns={4}>
            <DocField label='Requisition' value={requisitionCode} />
            <DocField label='Linked Defect' value={linkedDefect} />
            <DocField label={contextPrimaryLabel} value={contextPrimaryValue} />
            <DocField label={contextSecondaryLabel} value={contextSecondaryValue} />
          </DocFieldGrid>
        </DocBlock>

        <DocBlock title='Line Items'>
          <div className='table-responsive'>
            <table className='table table-bordered align-middle doc-tight mb-0 procurement-print-table'>
              <thead className='table-light'>
                <tr>
                  <th style={{width: '56px'}}>No</th>
                  <th>Item Description</th>
                  <th style={{width: '96px'}}>Expected</th>
                  <th style={{width: '96px'}}>Received</th>
                  <th style={{width: '110px'}}>Condition</th>
                </tr>
              </thead>
              <tbody>
                {resolvedLines.length === 0 ? (
                  <tr>
                    <td colSpan={5} className='text-center text-muted py-3'>
                      No line items available.
                    </td>
                  </tr>
                ) : (
                  resolvedLines.map((line, idx) => (
                    <tr key={`grn-line-${idx}`} className='procurement-avoid-break'>
                      <td>{idx + 1}</td>
                      <td>
                        <div>{line.itemDescription}</div>
                        {context === 'TECHNICAL' ? (
                          <div className='procurement-line-detail'>
                            {contextPrimaryLabel}: {displayValue(line.equipmentName)} | {contextSecondaryLabel}:{' '}
                            {displayValue(line.subcomponentName)} | Part: {displayValue(line.partName)} | Part No:{' '}
                            {line.partNo} | Drawing: {line.drawingNo}
                          </div>
                        ) : (
                          <div className='procurement-line-detail'>
                            {contextPrimaryLabel}: {contextPrimaryValue} | {contextSecondaryLabel}:{' '}
                            {contextSecondaryValue} | Part No: {line.partNo}
                          </div>
                        )}
                        {line.notes ? (
                          <div className='procurement-line-note'>
                            <span className='text-muted fw-semibold'>Notes:</span> {line.notes}
                          </div>
                        ) : null}
                      </td>
                      <td>{line.expectedQty ?? '-'}</td>
                      <td>{line.receivedQty ?? '-'}</td>
                      <td>{displayValue(line.condition)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </DocBlock>

        <DocBlock title='General Notes'>
          <div style={{whiteSpace: 'pre-wrap'}}>{displayValue(receipt.generalNotes)}</div>
        </DocBlock>

        <DocBlock title='Photos'>
          <ProcurementAttachmentPrintList
            attachments={(((receipt as any)?.attachments || []).length
              ? ((receipt as any)?.attachments || [])
              : (receipt.photos || [])
            ).map((attachment: any, index: number) => ({
              id: attachment?.id ?? null,
              registryId: attachment?.registryId ?? attachment?.id ?? null,
              attachmentKey: attachment?.attachmentKey ?? null,
              fileName: attachment?.fileName || `Photo ${index + 1}`,
              url: attachment?.url || '',
              viewUrl: attachment?.viewUrl || null,
              downloadUrl: attachment?.downloadUrl || null,
              sizeBytes: attachment?.sizeBytes ?? attachment?.size ?? 0,
              available: attachment?.available ?? null,
            }))}
            emptyText='No photos uploaded.'
          />
        </DocBlock>
      </div>
    </div>
  )
}

export default GoodsReceiptGRNDocument
